import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { theme } from "@polifonia/theme";
import { Link, Navigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import { useEnableWebstream } from "@/features/webstream/useEnableWebstream";
import { interlude, type InterludeTypes } from "@/interlude";
import { PageErrorBoundary } from "@/layout/ErrorBoundary/PageErrorBoundary";
import { useWebStreamConfigForm } from "@/pages/channels/hooks/form/useWebStreamConfigForm";
import {
  ChannelQueriesContext,
  ChannelQueriesContextProvider,
} from "@/pages/channels/pages/detail/channelQueriesContext";
import { ConfigurationTab } from "@/pages/channels/pages/detail/wccs/WebStreamConfig/ConfigurationTab";
import { GeneralTab } from "@/pages/channels/pages/detail/wccs/WebStreamConfig/GeneralTab";
import { PageTitle } from "@/pages/components/PageTitle";

type WebStreamConfig = InterludeTypes["WebStreamConfig"];

const LocaleKeyEnum = z.enum(["enUs", "th", "ja", "zhTw"]);

namespace WebStreamConfigPage {
  export type LocaleKey = z.infer<typeof LocaleKeyEnum>;
}

const WebStreamTabsSchema = z.enum(["general", "configuration"]);
type WebStreamTab = z.infer<typeof WebStreamTabsSchema>;

interface TabPanelProps {
  index: WebStreamTab;
  value: WebStreamTab;
  children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = function TabPanel({
  children,
  value,
  index,
}) {
  const selected = value === index;
  return (
    <Box
      role="tabpanel"
      hidden={!selected}
      id={`webstream-tabpanel-${index}`}
      aria-labelledby={`webstream-tab-${index}`}
    >
      {selected ? <Box sx={{ pt: "8px" }}>{children}</Box> : null}
    </Box>
  );
};

const WebStreamConfigDetail: React.FC = function WebStreamConfigDetail() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { channel } = ChannelQueriesContext.useData();
  const [value, setValue] = useState<WebStreamTab>(
    WebStreamTabsSchema.Enum.general,
  );
  // The tab the user is trying to reach while the Configuration form has
  // unsaved edits; non-null means the "unsaved changes" modal is open.
  const [pendingTab, setPendingTab] = useState<WebStreamTab | null>(null);

  // The form lives here (above the tabs) so its state survives tab switches
  // and the tab bar can read `isDirty` to guard against losing unsaved edits.
  const webStreamConfigForm = useWebStreamConfigForm();
  const putWebStreamConfigMutation = interlude.channel.usePutWebStreamConfig(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess: () => {
        enqueueSnackbar(t("common.updatedSuccessfully"), {
          variant: "success",
        });
      },
    },
  );

  const saveConfig = useHandler(
    (config: WebStreamConfig, onSaved?: VoidFunction) => {
      putWebStreamConfigMutation.mutate(config, {
        onSuccess: () => {
          // Re-baseline the form to the saved values so it is no longer dirty.
          webStreamConfigForm.reset(config);
          onSaved?.();
        },
      });
    },
  );

  const handleValidSubmit = useHandler((config: WebStreamConfig) => {
    saveConfig(config);
  });

  // Read `isDirty` during render so RHF's formState proxy actually tracks it —
  // fields not accessed in render aren't subscribed, and the tab guard below
  // would otherwise read a stale value from its event handler.
  const { isDirty } = webStreamConfigForm.formState;

  const handleTabChange = useHandler<
    React.ComponentProps<typeof Tabs>["onChange"]
  >((_, newValue) => {
    const result = WebStreamTabsSchema.safeParse(newValue);
    if (!result.success || result.data === value) return;
    // Guard only when leaving the Configuration tab with unsaved edits.
    if (value === WebStreamTabsSchema.Enum.configuration && isDirty) {
      setPendingTab(result.data);
      return;
    }
    setValue(result.data);
  });

  const closeModal = useHandler(() => {
    setPendingTab(null);
  });

  const handleModalDiscard = useHandler(() => {
    if (!pendingTab) return;
    // Drop the unsaved edits back to the last loaded/saved config, then leave.
    webStreamConfigForm.reset();
    setValue(pendingTab);
    setPendingTab(null);
  });

  const handleModalSave = useHandler(() => {
    const target = pendingTab;
    webStreamConfigForm.handleSubmit(
      (config) => {
        saveConfig(config, () => {
          if (target) setValue(target);
          setPendingTab(null);
        });
      },
      // On validation error, close the modal so the user can see and fix the
      // errors on the (still-active) Configuration tab.
      () => setPendingTab(null),
    )();
  });

  return (
    <FormProvider {...webStreamConfigForm}>
      <Stack gap="16px">
        <Breadcrumbs aria-label="breadcrumb" separator=">">
          <Link to="/channels" style={{ textDecoration: "none" }}>
            <Typography variant="body" color={theme.colors.staticFgBody}>
              {t("channels.title")}
            </Typography>
          </Link>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("webStreamConfig.title")}
          </Typography>
        </Breadcrumbs>
        <PageTitle>{t("webStreamConfig.title")}</PageTitle>
        <Tabs value={value} onChange={handleTabChange}>
          <Tab
            label={t("page.channel.tabs.general")}
            value={WebStreamTabsSchema.Values.general}
          />
          <Tab
            label={t("webStreamConfig.tabs.configuration")}
            value={WebStreamTabsSchema.Values.configuration}
          />
        </Tabs>
        <TabPanel value={value} index={WebStreamTabsSchema.Enum.general}>
          <GeneralTab />
        </TabPanel>
        <TabPanel value={value} index={WebStreamTabsSchema.Enum.configuration}>
          <ConfigurationTab
            onValidSubmit={handleValidSubmit}
            isSaving={putWebStreamConfigMutation.isPending}
          />
        </TabPanel>
      </Stack>
      <Modal
        open={pendingTab !== null}
        handleClose={closeModal}
        title={t("webStreamConfig.unsavedModal.title")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="16px">
            <Button
              variant="plain"
              onClick={closeModal}
              disabled={putWebStreamConfigMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="plain"
              onClick={handleModalDiscard}
              disabled={putWebStreamConfigMutation.isPending}
            >
              {t("webStreamConfig.unsavedModal.discard")}
            </Button>
            <Button
              variant="primary"
              onClick={handleModalSave}
              loading={putWebStreamConfigMutation.isPending}
              disabled={putWebStreamConfigMutation.isPending}
            >
              {t("webStreamConfig.unsavedModal.save")}
            </Button>
          </Stack>
        }
      >
        <Trans i18nKey="webStreamConfig.unsavedModal.description" />
      </Modal>
    </FormProvider>
  );
};

// useWebStreamConfigForm() fires its own GET for the web-stream config, so this
// guard must confirm the channel is a valid web-streams wccs channel *before*
// rendering the detail, not after.
const WebStreamConfigGuard: React.FC = function WebStreamConfigGuard() {
  const { channel } = ChannelQueriesContext.useData();

  if (
    channel.type !== "wccs" ||
    channel.channelInformation.type !== "web-streams"
  ) {
    return <Navigate to="/channels" replace />;
  }

  return <WebStreamConfigDetail />;
};

const WebStreamConfigPage: React.FC = function WebStreamConfigPage() {
  const enableWebstream = useEnableWebstream();

  if (!enableWebstream) {
    return <Navigate to="/channels" replace />;
  }

  return (
    <PageErrorBoundary>
      <Suspense fallback={<BarLoading />}>
        <ChannelQueriesContextProvider>
          <WebStreamConfigGuard />
        </ChannelQueriesContextProvider>
      </Suspense>
    </PageErrorBoundary>
  );
};

export { LocaleKeyEnum, WebStreamConfigPage };
